import { useAppNavigate } from "hooks/useAppNavigate";
import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { recaptchaSiteKey } from "env";
import LoadingButton from "components/LoadingButton";
import PasswordInput from "components/PasswordInput";
import ErrorMessage from "components/ErrorMessage";
import RecaptchaField, { RecaptchaFieldRef } from "components/RecaptchaField";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import LinkButton from "components/LinkButton";
import { loginWithPassword, isRecaptchaRequiredError, isServiceUnavailableError } from "services/auth.service";
import { setUserData, getFlowState, setJwtTokens } from "../../storage";
import { useLoginPage } from "hooks/useLoginPage";
import { useClearStorageOnQueryParam } from "hooks/useClearStorageOnQueryParam";
import { isValidEmail } from "utils/validators";
import { postAuthTokenToNative } from "utils/nativeBridge";
import TextInput from "components/TextInput";
import { useText } from "context/TextProvider";
import { trackEvent } from "services/analytics.service";
import { AnalyticsEvent, AnalyticsFlow, AnalyticsMethod, AnalyticsStatus } from "services/analyticsEvents";
import { useScreenTimeTracking } from "hooks/useScreenTimeTracking";

const PasswordLogin = (): JSX.Element => {
  const text = useText();
  const appNavigate = useAppNavigate();
  const { configId, loginPageConfig } = useLoginPage();
  useClearStorageOnQueryParam();
  useScreenTimeTracking(AnalyticsEvent.TIME_ON_PASSWORD_LOGIN_SCREEN);
  const [formData, setFormData] = useState({
    email: getFlowState(configId).email || "",
    password: "",
  });
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const loginStartedAt = useRef<number | null>(null);
  const recaptchaRef = useRef<RecaptchaFieldRef>(null);
  const enableRecaptcha = loginPageConfig?.enableRecaptcha ?? false;

  const onChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !isValidEmail(formData.email)) {
      setErrorMessage(text.passwordLogin.invalidEmail);
      return;
    }
    if (!formData.password) {
      setErrorMessage(text.passwordLogin.passwordRequired);
      return;
    }
    if (enableRecaptcha && !recaptchaToken) {
      setErrorMessage(text.passwordLogin.recaptchaRequired);
      return;
    }
    setLoading(true);
    setErrorMessage("");
    trackEvent(AnalyticsEvent.PASSWORD_LOGIN_STARTED, { method: AnalyticsMethod.PASSWORD, flow: AnalyticsFlow.STANDARD, status: AnalyticsStatus.STARTED });
    loginStartedAt.current = Date.now();
    try {
      const data = await loginWithPassword({
        loginId: formData.email,
        password: formData.password,
        recaptchaToken,
        enableRecaptcha,
        url: loginPageConfig?.urls?.login_with_password,
      });
      if (data.changePasswordId) {
        appNavigate(`/password/force-change/${data.changePasswordId}`);
        return;
      }
      setUserData(data);
      const { user, ...tokens } = data;
      setJwtTokens(tokens);
      trackEvent(AnalyticsEvent.PASSWORD_LOGIN_SUCCEEDED, { method: AnalyticsMethod.PASSWORD, flow: AnalyticsFlow.STANDARD, status: AnalyticsStatus.SUCCEEDED });
      if (loginStartedAt.current) {
        trackEvent(AnalyticsEvent.TIME_TO_LOGIN, { duration_ms: Date.now() - loginStartedAt.current });
      }
      postAuthTokenToNative({
        token: data.token,
        refreshToken: data.refreshToken,
        tokenExpirationInstant: data.tokenExpirationInstant,
        refreshUrl: loginPageConfig?.urls?.refresh_url,
      });
      appNavigate(`/`);
    } catch (err) {
      setErrorMessage(
        isRecaptchaRequiredError(err)
          ? text.passwordLogin.recaptchaRequired
          : isServiceUnavailableError(err)
          ? text.common.serviceUnavailable
          : text.passwordLogin.loginFailed
      );
      trackEvent(AnalyticsEvent.PASSWORD_LOGIN_FAILED, { method: AnalyticsMethod.PASSWORD, flow: AnalyticsFlow.STANDARD, status: AnalyticsStatus.FAILED });
      // The spent token can't be reused, but the widget still shows as
      // checked — reset it so the user can re-solve immediately instead of
      // waiting for it to expire on its own.
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
      setRecaptchaToken("");
    }
  };

  return (
    <>
      <PageMeta name="Password Login" />
      <PageTitle>{text.passwordLogin.title}</PageTitle>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <TextInput
        type="email"
        label={text.passwordLogin.emailLabel}
        value={formData.email}
        placeholder={text.passwordLogin.emailPlaceholder}
        onChange={(e) => onChange(e, "email")}
        disabled={loading}
      />
      <PasswordInput
        label={text.passwordLogin.passwordLabel}
        value={formData.password}
        placeholder={text.passwordLogin.passwordPlaceholder}
        onChange={(e) => onChange(e, "password")}
        disabled={loading}
      />

      {enableRecaptcha && (
        <RecaptchaField
          ref={recaptchaRef}
          sitekey={recaptchaSiteKey}
          onChange={(token) => setRecaptchaToken(token ?? "")}
          onError={(message) => setErrorMessage(message)}
          theme="dark"
          size="normal"
          tabIndex={0}
        />
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "0.5rem" }}>
        <LinkButton
          type="button"
          disabled={loading}
          onClick={() =>
            appNavigate(`/forgot-password`, {state: { email: formData.email } })
          }
          sx={{ fontSize: "0.75rem" }}
        >
          {text.passwordLogin.forgotPasswordLink}
        </LinkButton>
      </Box>
      <LoadingButton
        loading={loading}
        onClick={handleLogin}
        disabled={loading}
        sx={{
          width: "100%",
          height: "3.5rem",
          mb: "1rem",
          mt: "1.5rem",
        }}
      >
        {text.passwordLogin.loginButton}
      </LoadingButton>
      <Box sx={{ textAlign: "center" }}>
        <Typography component="span" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
          {text.passwordLogin.troubleLoggingIn}
        </Typography>
        <LinkButton
          onClick={() => appNavigate(`/login`)}
          sx={{ fontSize: "0.875rem", fontWeight: 600 }}
        >
          {text.passwordLogin.loginWithCodeButton}
        </LinkButton>
      </Box>
    </>
  );
};

export default PasswordLogin;
